import {
  CollapsiblePanel,
  type GroupImperativeHandle,
  KeepMountedPanel,
  Panel,
  PanelGroup,
  PanelHandle,
  SetMetadata,
  useAutoCollapseByWidth,
  useCollapsiblePanelController,
} from "@qw/ui";
import { Fragment, useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Outlet } from "react-router";
import { ChatColumn } from "@/chat/chat-column";
import { Conversation } from "@/conversation/conversation";
import { ChatPosition } from "@/providers/page-provider";
import { LeftSidebar } from "@/sidebar/left-sidebar";
import { useInboxQueryStates } from "../providers/inbox-query-states-provider";
import { useBook } from "../providers/use-book";
import { useConversation } from "../providers/use-conversation";
import { usePage } from "../providers/use-page";
import { Inbox } from "./inbox";
import { INBOX_LIST_THREAD_LAYOUT } from "./inbox-list-thread-layout";
import {
  areLayoutsEqual,
  buildInboxShellLayout,
  CONVERSATION_PANEL_MIN_WIDTH_PX,
  getContainerWidth,
  getContentMinWidthPx,
  getInboxShellPanelWidthFromLayout,
  getInboxShellSidePanelWidthsFromLayout,
  getInboxSidePanelMaxWidthsPx,
  getPercentLayout,
  hasLayoutForPanelIds,
  INBOX_LIST_BESIDE_MESSAGE_MIN_PX,
  INBOX_PANEL_MIN_WIDTH_PX,
  INBOX_SIDE_PANEL_DEFAULT_WIDTHS_PX,
  INBOX_SIDE_PANEL_MIN_WIDTHS_PX,
  type InboxShellPanelId,
  LAYOUT_WIDTH_TOLERANCE_PX,
  mergeInboxSidePanelWidths,
  readInboxSidePanelWidths,
  useElementWidth,
  writeInboxSidePanelWidths,
} from "./inbox-shell-layout";
import { useInboxListThreadLayout } from "./use-inbox-list-thread-layout";
import { INBOX_OUTLET_HOST, useInboxRail } from "./use-inbox-rail";

export {
  buildInboxShellLayout,
  getContentMinWidthPx,
  getInboxShellPanelWidthFromLayout,
  getInboxShellSidePanelWidthsFromLayout,
  sanitizeInboxSidePanelWidths,
} from "./inbox-shell-layout";

type InboxContentPanelId = "inbox" | "conversation";
type StackedListThreadPanelId = "inbox" | "conversation";

const INBOX_PANELS_AUTOSAVE_ID = "inbox-panels";
const INBOX_STACKED_LIST_THREAD_PANELS_AUTOSAVE_ID = "inbox-stacked-list-thread-panels";

const INBOX_CONTENT_PANEL_DEFAULT_SIZES: Record<InboxContentPanelId, number> = {
  conversation: 30,
  inbox: 22,
};

const getInboxContentDefaultLayout = (panelIds: InboxContentPanelId[]) =>
  getPercentLayout(panelIds, INBOX_CONTENT_PANEL_DEFAULT_SIZES);

const STACKED_LIST_THREAD_DEFAULT_LAYOUT: Record<StackedListThreadPanelId, number> = {
  inbox: 38,
  conversation: 62,
};

export const InboxLayout = () => {
  const { isBookOpen } = useBook();
  const { conversationId, error, inboxConversation, loading } = useConversation();
  const { conversationId: selectedConversationId } = useInboxQueryStates();
  const { chatPosition, isChatOpen } = usePage();
  const { isMergedRail, outletHost } = useInboxRail();
  const { listThreadLayout } = useInboxListThreadLayout();
  const isSideChat = chatPosition === ChatPosition.side;
  const isChatPanelVisible = isSideChat && isChatOpen;
  const hasConversationPanel = Boolean(
    selectedConversationId || conversationId || inboxConversation || loading || error
  );
  const hasMiniAppPanel = outletHost === INBOX_OUTLET_HOST.miniApp;
  const isStackedListThreadLayout = listThreadLayout === INBOX_LIST_THREAD_LAYOUT.stacked && hasConversationPanel;

  const shellGroupRef = useRef<GroupImperativeHandle | null>(null);
  const contentGroupRef = useRef<GroupImperativeHandle | null>(null);
  const shellContainerRef = useRef<HTMLDivElement | null>(null);
  const programmaticShellLayoutsRef = useRef<Record<string, number>[]>([]);
  // `handleShowInbox` builds with a wider content minimum than `shellDefaultLayout`,
  // so the widths it fits are a second layout the shell built and must not persist.
  // It stops describing the rendered shell once the sync effect rebuilds it.
  const restoreShellLayoutRef = useRef<Record<string, number> | null>(null);
  const [shellContainerElement, setShellContainerElement] = useState<HTMLDivElement | null>(null);
  const [sidePanelWidths, setSidePanelWidths] = useState(readInboxSidePanelWidths);
  const inbox = useCollapsiblePanelController();
  const contentMinWidthPx = getContentMinWidthPx(hasConversationPanel);
  const shellContainerWidth = useElementWidth(shellContainerElement);
  const chatPanelId: InboxShellPanelId = isChatPanelVisible ? "chat" : "chat-mount";
  // The drag limits the panels declare and the widths the shell computes read
  // the same record, so the two can never disagree about how wide the rail goes.
  const sidePanelMaxWidthsPx = getInboxSidePanelMaxWidthsPx(isMergedRail);

  const shellPanelIds = useMemo<InboxShellPanelId[]>(() => {
    const panelIds: InboxShellPanelId[] = [];
    if (isBookOpen) {
      panelIds.push("book");
    }
    panelIds.push("content");
    if (hasMiniAppPanel) {
      panelIds.push("mini-app");
    }
    panelIds.push(chatPanelId);
    return panelIds;
  }, [chatPanelId, hasMiniAppPanel, isBookOpen]);

  const contentPanelIds = useMemo<InboxContentPanelId[]>(() => {
    const panelIds: InboxContentPanelId[] = ["inbox"];
    if (hasConversationPanel) {
      panelIds.push("conversation");
    }
    return panelIds;
  }, [hasConversationPanel]);

  const { containerRef, onLayoutChange } = useAutoCollapseByWidth({
    controller: inbox,
    groupRef: contentGroupRef,
    reservedPanelIds: [],
    minWidthPx: INBOX_LIST_BESIDE_MESSAGE_MIN_PX - LAYOUT_WIDTH_TOLERANCE_PX,
    targetPanelId: "inbox",
    minTargetPanelWidthPx: INBOX_PANEL_MIN_WIDTH_PX - LAYOUT_WIDTH_TOLERANCE_PX,
    enabled: hasConversationPanel && listThreadLayout === INBOX_LIST_THREAD_LAYOUT.sideBySide,
  });

  const setShellContainerRef = useCallback((element: HTMLDivElement | null) => {
    shellContainerRef.current = element;
    setShellContainerElement(element);
  }, []);

  const shellDefaultLayout = useMemo(
    () =>
      buildInboxShellLayout({
        panelIds: shellPanelIds,
        sidePanelWidths,
        containerWidthPx: shellContainerWidth,
        contentMinWidthPx,
        sidePanelMaxWidthsPx,
      }),
    [contentMinWidthPx, shellContainerWidth, shellPanelIds, sidePanelMaxWidthsPx, sidePanelWidths]
  );
  const contentDefaultLayout = useMemo(
    () =>
      isStackedListThreadLayout ? STACKED_LIST_THREAD_DEFAULT_LAYOUT : getInboxContentDefaultLayout(contentPanelIds),
    [contentPanelIds, isStackedListThreadLayout]
  );
  const canShowInboxBesideMessage = useMemo(() => {
    if (shellContainerWidth <= 0) {
      return false;
    }

    const restoreLayout = buildInboxShellLayout({
      panelIds: shellPanelIds,
      sidePanelWidths,
      containerWidthPx: shellContainerWidth,
      contentMinWidthPx: INBOX_LIST_BESIDE_MESSAGE_MIN_PX,
      sidePanelMaxWidthsPx,
    });
    const restoreContentWidthPx = getInboxShellPanelWidthFromLayout({
      panelIds: shellPanelIds,
      layout: restoreLayout,
      panelId: "content",
      containerWidthPx: shellContainerWidth,
    });

    return restoreContentWidthPx >= INBOX_LIST_BESIDE_MESSAGE_MIN_PX - LAYOUT_WIDTH_TOLERANCE_PX;
  }, [shellContainerWidth, shellPanelIds, sidePanelMaxWidthsPx, sidePanelWidths]);

  const setProgrammaticShellLayout = useCallback((group: GroupImperativeHandle, nextLayout: Record<string, number>) => {
    programmaticShellLayoutsRef.current = [...programmaticShellLayoutsRef.current, nextLayout].slice(-4);
    group.setLayout(nextLayout);
  }, []);

  useLayoutEffect(() => {
    const group = shellGroupRef.current;

    restoreShellLayoutRef.current = null;

    if (!group || shellContainerWidth <= 0) {
      return;
    }

    const nextLayout = buildInboxShellLayout({
      panelIds: shellPanelIds,
      sidePanelWidths,
      containerWidthPx: shellContainerWidth,
      contentMinWidthPx,
      sidePanelMaxWidthsPx,
    });

    const currentLayout = group.getLayout();

    if (!hasLayoutForPanelIds(currentLayout, shellPanelIds)) {
      return;
    }

    if (!areLayoutsEqual(currentLayout, nextLayout)) {
      setProgrammaticShellLayout(group, nextLayout);
    }
  }, [
    contentMinWidthPx,
    setProgrammaticShellLayout,
    shellContainerWidth,
    shellPanelIds,
    sidePanelMaxWidthsPx,
    sidePanelWidths,
  ]);

  const handleShellLayoutChanged = useCallback(
    (layout: Record<string, number>) => {
      const programmaticLayoutIndex = programmaticShellLayoutsRef.current.findIndex((programmaticLayout) =>
        areLayoutsEqual(layout, programmaticLayout)
      );

      if (programmaticLayoutIndex >= 0) {
        programmaticShellLayoutsRef.current = programmaticShellLayoutsRef.current.slice(programmaticLayoutIndex + 1);
        return;
      }

      if (programmaticShellLayoutsRef.current.length > 0) {
        programmaticShellLayoutsRef.current = [];
      }

      const containerWidthPx = getContainerWidth(shellContainerRef.current);
      const getWidths = (fromLayout: Record<string, number>) =>
        getInboxShellSidePanelWidthsFromLayout({ panelIds: shellPanelIds, layout: fromLayout, containerWidthPx });
      const nextWidths = getWidths(layout);
      const builtWidths = restoreShellLayoutRef.current
        ? [getWidths(shellDefaultLayout), getWidths(restoreShellLayoutRef.current)]
        : [getWidths(shellDefaultLayout)];

      setSidePanelWidths((currentWidths) => {
        const mergedWidths = mergeInboxSidePanelWidths(currentWidths, nextWidths, builtWidths);

        if (mergedWidths !== currentWidths) {
          writeInboxSidePanelWidths(mergedWidths);
        }

        return mergedWidths;
      });
    },
    [shellDefaultLayout, shellPanelIds]
  );

  const handleContentLayoutChange = useCallback(() => {
    onLayoutChange();
  }, [onLayoutChange]);

  const handleContentLayoutChanged = useCallback(() => {
    onLayoutChange();
  }, [onLayoutChange]);

  const handleShowInbox = useCallback(() => {
    if (!canShowInboxBesideMessage) {
      return;
    }

    const shellContainerWidth = getContainerWidth(shellContainerRef.current);
    const shellGroup = shellGroupRef.current;
    const contentGroup = contentGroupRef.current;

    if (shellGroup && shellContainerWidth > 0) {
      const nextLayout = buildInboxShellLayout({
        panelIds: shellPanelIds,
        sidePanelWidths,
        containerWidthPx: shellContainerWidth,
        contentMinWidthPx: INBOX_LIST_BESIDE_MESSAGE_MIN_PX,
        sidePanelMaxWidthsPx,
      });

      restoreShellLayoutRef.current = nextLayout;
      setProgrammaticShellLayout(shellGroup, nextLayout);

      const contentWidthPx = getInboxShellPanelWidthFromLayout({
        panelIds: shellPanelIds,
        layout: nextLayout,
        panelId: "content",
        containerWidthPx: shellContainerWidth,
      });

      if (contentGroup && contentWidthPx > 0) {
        const inboxPanelPercent = Math.min(100, (INBOX_PANEL_MIN_WIDTH_PX / contentWidthPx) * 100);
        contentGroup.setLayout({
          inbox: inboxPanelPercent,
          conversation: 100 - inboxPanelPercent,
        });
      } else {
        inbox.panelRef.current?.resize(`${INBOX_PANEL_MIN_WIDTH_PX}px`);
      }

      return;
    }

    inbox.panelRef.current?.resize(`${INBOX_PANEL_MIN_WIDTH_PX}px`);
  }, [
    canShowInboxBesideMessage,
    inbox.panelRef,
    setProgrammaticShellLayout,
    shellPanelIds,
    sidePanelMaxWidthsPx,
    sidePanelWidths,
  ]);

  const inboxDefaultSize = `${INBOX_CONTENT_PANEL_DEFAULT_SIZES.inbox}%`;
  const conversationDefaultSize = `${INBOX_CONTENT_PANEL_DEFAULT_SIZES.conversation}%`;
  const inboxElement = <Inbox />;

  return (
    <>
      {inboxConversation && <SetMetadata key={inboxConversation.id} title={inboxConversation.subject} />}
      <div ref={setShellContainerRef} className="h-full w-full">
        <PanelGroup
          direction="horizontal"
          defaultLayout={shellDefaultLayout}
          groupRef={shellGroupRef}
          onLayoutChanged={handleShellLayoutChanged}
          panelIds={shellPanelIds}
        >
          {isBookOpen && (
            <Fragment key="book-slot">
              <Panel
                key="book"
                id="book"
                minSize={`${INBOX_SIDE_PANEL_MIN_WIDTHS_PX.book}px`}
                maxSize={`${sidePanelMaxWidthsPx.book}px`}
                defaultSize={`${INBOX_SIDE_PANEL_DEFAULT_WIDTHS_PX.book}px`}
                className="overflow-hidden"
              >
                <LeftSidebar withBorder={false} />
              </Panel>
              <PanelHandle key="book-handle" />
            </Fragment>
          )}
          <Panel
            key="content"
            id="content"
            minSize={`${contentMinWidthPx}px`}
            defaultSize={`${contentMinWidthPx}px`}
            className="flex min-h-0 min-w-0 overflow-hidden"
          >
            <div ref={containerRef} className="h-full min-h-0 w-full min-w-0">
              <PanelGroup
                direction={isStackedListThreadLayout ? "vertical" : "horizontal"}
                autoSaveId={
                  isStackedListThreadLayout ? INBOX_STACKED_LIST_THREAD_PANELS_AUTOSAVE_ID : INBOX_PANELS_AUTOSAVE_ID
                }
                defaultLayout={contentDefaultLayout}
                groupRef={contentGroupRef}
                onLayoutChange={handleContentLayoutChange}
                onLayoutChanged={handleContentLayoutChanged}
                panelIds={contentPanelIds}
                className="min-h-0 min-w-0"
              >
                {isStackedListThreadLayout ? (
                  <>
                    <Panel id="inbox" minSize="24%" defaultSize="38%" className="overflow-hidden">
                      {inboxElement}
                    </Panel>
                    <PanelHandle />
                    <Panel id="conversation" minSize="32%" defaultSize="62%" className="overflow-hidden">
                      <Conversation />
                    </Panel>
                  </>
                ) : (
                  <>
                    <CollapsiblePanel
                      key="inbox-slot"
                      controller={inbox}
                      id="inbox"
                      minSize={`${INBOX_PANEL_MIN_WIDTH_PX}px`}
                      defaultSize={inboxDefaultSize}
                      className="overflow-hidden"
                    >
                      {inboxElement}
                    </CollapsiblePanel>
                    {hasConversationPanel && (
                      <Fragment key="conversation-slot">
                        <PanelHandle key="conversation-handle" hidden={inbox.isCollapsed} />
                        <Panel
                          key="conversation"
                          id="conversation"
                          minSize={`${CONVERSATION_PANEL_MIN_WIDTH_PX}px`}
                          defaultSize={conversationDefaultSize}
                          className="overflow-hidden"
                        >
                          <Conversation
                            canShowInboxBesideMessage={canShowInboxBesideMessage}
                            onShowInbox={handleShowInbox}
                            showBackButton={inbox.isCollapsed}
                          />
                        </Panel>
                      </Fragment>
                    )}
                  </>
                )}
              </PanelGroup>
            </div>
          </Panel>
          {hasMiniAppPanel && (
            <>
              <PanelHandle key="mini-app-handle" />
              <Panel
                key="mini-app"
                id="mini-app"
                minSize={`${INBOX_SIDE_PANEL_MIN_WIDTHS_PX["mini-app"]}px`}
                maxSize={`${sidePanelMaxWidthsPx["mini-app"]}px`}
                defaultSize={`${INBOX_SIDE_PANEL_DEFAULT_WIDTHS_PX["mini-app"]}px`}
                className="overflow-hidden"
              >
                <Outlet />
              </Panel>
            </>
          )}
          <PanelHandle key="chat-handle" hidden={!isChatPanelVisible} />
          <KeepMountedPanel
            key="chat-slot"
            id="chat"
            hidden={!isChatPanelVisible}
            minSize={`${INBOX_SIDE_PANEL_MIN_WIDTHS_PX.chat}px`}
            maxSize={`${sidePanelMaxWidthsPx.chat}px`}
            defaultSize={`${INBOX_SIDE_PANEL_DEFAULT_WIDTHS_PX.chat}px`}
            className="overflow-hidden"
          >
            <ChatColumn layout={isChatPanelVisible ? "panel" : "flex"} mountOnly={!isChatOpen} />
          </KeepMountedPanel>
        </PanelGroup>
        {outletHost === INBOX_OUTLET_HOST.hidden && (
          <div className="hidden">
            <Outlet />
          </div>
        )}
      </div>
    </>
  );
};
